import {
  ListFilterValues,
  dateKey,
  fromKey,
  presetKey,
  toKey,
} from "./listFilters.types";

/**
 * The relative ranges the legacy date filters offered, in the same words. The maths is
 * ported from `getDateRangeDateValues` in `page/search/dateRangeValues` rather than
 * imported: that file reaches the whole legacy element graph, which `toSearchFilters`
 * already documents staying out of.
 *
 * A preset is a way of filling the From/To pair, not a value the filter is applied by -
 * the pair is what everything downstream reads, and it is always on show.
 */
export type DateRangePreset =
  | ""
  | "custom"
  | "today"
  | "yesterday"
  | "last-7-days"
  | "this-month"
  | "last-month"
  | "this-year"
  | "last-year";

/** "" is the placeholder - any time - and "custom" is whatever was typed into the pair. */
export const DATE_RANGE_PRESET_OPTIONS = [
  { Key: "today", Value: "Today" },
  { Key: "yesterday", Value: "Yesterday" },
  { Key: "last-7-days", Value: "Last 7 Days" },
  { Key: "this-month", Value: "This Month" },
  { Key: "last-month", Value: "Last Month" },
  { Key: "this-year", Value: "This Year" },
  { Key: "last-year", Value: "Last Year" },
  { Key: "custom", Value: "Custom" },
];

export const ANY_TIME_LABEL = "Any time";
export const CUSTOM_PRESET = "custom";

/** A local date as `yyyy-MM-dd`, which is how every filter value holds a day. */
export function toDay(date: Date) {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * A day filter's day and time window, as `yyyy-MM-dd` and `HH:mm`. Today and the whole
 * day when left empty, since the search it serves won't run without a day.
 */
export function dayBounds(
  values: ListFilterValues,
  name: string,
  today = new Date()
) {
  return {
    day: String(values[dateKey(name)] ?? "") || toDay(today),
    from: String(values[fromKey(name)] ?? "") || "00:00",
    to: String(values[toKey(name)] ?? "") || "23:59",
  };
}

/**
 * The days a preset covers, or null for one that names no range - any time, custom, or
 * anything unrecognised. `today` is a parameter so this can be tested.
 */
export function resolvePreset(preset: string, today = new Date()) {
  const year = today.getFullYear();
  const month = today.getMonth();
  const date = today.getDate();
  const day = (offset: number) => toDay(new Date(year, month, date + offset));

  switch (preset) {
    case "today":
      return { from: day(0), to: day(0) };
    case "yesterday":
      return { from: day(-1), to: day(-1) };
    // Eight days inclusive, as the legacy range was: today minus seven, to today.
    case "last-7-days":
      return { from: day(-7), to: day(0) };
    case "this-month":
      return {
        from: toDay(new Date(year, month, 1)),
        // Day 0 of the next month is the last day of this one.
        to: toDay(new Date(year, month + 1, 0)),
      };
    case "last-month":
      return {
        from: toDay(new Date(year, month - 1, 1)),
        to: toDay(new Date(year, month, 0)),
      };
    case "this-year":
      return { from: `${year}-01-01`, to: `${year}-12-31` };
    case "last-year":
      return { from: `${year - 1}-01-01`, to: `${year - 1}-12-31` };
    default:
      return null;
  }
}

/**
 * The three values a range holds once a preset is picked: the preset itself, and the two
 * days it fills the pair with. Custom and any time leave the pair as the reader left it
 * and as empty respectively.
 */
export function presetRangeValues(
  name: string,
  preset: string,
  current: ListFilterValues
): ListFilterValues {
  const resolved = resolvePreset(preset);

  if (resolved) {
    return {
      [presetKey(name)]: preset,
      [fromKey(name)]: resolved.from,
      [toKey(name)]: resolved.to,
    };
  }

  if (preset === CUSTOM_PRESET) {
    return {
      [presetKey(name)]: preset,
      [fromKey(name)]: current[fromKey(name)] ?? "",
      [toKey(name)]: current[toKey(name)] ?? "",
    };
  }

  return {
    [presetKey(name)]: "",
    [fromKey(name)]: "",
    [toKey(name)]: "",
  };
}

/**
 * The three values a range holds once one end of the pair is typed into. The preset
 * follows the dates rather than the other way round, so a day that no relative range
 * would have produced says Custom; emptying both says any time again.
 */
export function typedRangeValues(
  name: string,
  key: string,
  day: string,
  current: ListFilterValues
): ListFilterValues {
  const pair = {
    [fromKey(name)]: current[fromKey(name)] ?? "",
    [toKey(name)]: current[toKey(name)] ?? "",
    [key]: day,
  };
  const empty = pair[fromKey(name)] === "" && pair[toKey(name)] === "";

  return { ...pair, [presetKey(name)]: empty ? "" : CUSTOM_PRESET };
}

function asDay(value: unknown) {
  return typeof value === "string" ? value : "";
}

/**
 * The days a date-range filter covers. Always the pair: a preset has already been
 * resolved into it by the time anything filters on it.
 */
export function rangeBounds(values: ListFilterValues, name: string) {
  return {
    from: asDay(values[fromKey(name)]),
    to: asDay(values[toKey(name)]),
  };
}
